// pages/Events.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Icon from "../../components/AppIcon";
import {Breadcrumb} from "components/ui/Breadcrumb";
import { formatCurrency } from "utils/localization";
import apiService from "services/api";

const Events = () => {
  const [events, setEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch events from API
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const response = await apiService.getEvents();
        if (response.success) {
          // Map API response to expected format
          const mappedEvents = response.data.events.map(event => ({
            id: event.id,
            title: event.name,
            description: event.description || "No description provided",
            totalAmount: event.totalExpenses || 0,
            participants: event.participantCount || 0,
            status: event.status || "active",
            createdDate: event.date || event.createdAt,
            settledAmount: 0, // We might need to calculate this based on balances
            image: null, // API doesn't provide images
          }));
          setEvents(mappedEvents);
        } else {
          setError(response.message || "Failed to fetch events");
        }
      } catch (err) {
        console.error("Error fetching events:", err);
        setError(err.message || "Failed to fetch events");
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

 const filteredEvents = events.filter((event) => {
    const matchesSearch =
      event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter =
      filterStatus === "all" || event.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusBadge = (status) => {
    const statusStyles = {
      active: "bg-blue-100 text-blue-800 border-blue-200",
      settled: "bg-green-100 text-green-800 border-green-200",
      pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
      cancelled: "bg-destructive/10 text-destructive border-destructive/20",
    };

    return (
      <span
        className={`px-2 py-1 text-xs font-medium rounded-full border ${statusStyles[status] || statusStyles.active}`}
      >
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

 if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pt-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <Icon
            name="AlertCircle"
            size={48}
            className="mx-auto text-destructive mb-4"
          />
          <h3 className="text-lg font-medium text-foreground mb-2">Error Loading Events</h3>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Link
            to="/create-event"
            className="inline-flex items-center space-x-2 bg-primary text-primary-foreground px-6 py-3 rounded-md font-medium hover:bg-primary/90 transition-colors"
          >
            <Icon name="Plus" size={20} />
            <span>Create New Event</span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16">
      <div className="space-y-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumb />
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Events</h1>
            <p className="text-muted-foreground mt-2">
              Manage and track your shared expenses
            </p>
          </div>
          <Link
            to="/create-event"
            className="flex items-center space-x-2 bg-primary text-primary-foreground px-4 py-2 rounded-md font-medium hover:bg-primary/90 transition-colors"
          >
            <Icon name="Plus" size={20} />
            <span>Create Event</span>
          </Link>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Icon
              name="Search"
              size={20}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="text"
              placeholder="Search events..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="settled">Settled</option>
            <option value="pending">Pending</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {/* Events Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEvents.map((event) => (
            <Link
              key={event.id}
              to={`/events/${event.id}`}
              className="bg-card border border-border rounded-lg p-6 hover:shadow-lg transition-all duration-200 hover:border-primary/50"
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-foreground line-clamp-1">
                  {event.title}
                </h3>
                {getStatusBadge(event.status)}
              </div>

              <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                {event.description}
              </p>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">
                    Total Amount
                  </span>
                  <span className="font-semibold text-foreground">
                    {formatCurrency(event.totalAmount)}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Settled</span>
                  <span className="font-medium text-green-600">
                    {formatCurrency(event.settledAmount)}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">
                    Participants
                  </span>
                  <div className="flex items-center space-x-1">
                    <Icon
                      name="Users"
                      size={16}
                      className="text-muted-foreground"
                    />
                    <span className="text-sm font-medium">
                      {event.participants}
                    </span>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Created</span>
                  <span className="text-sm font-medium">
                    {new Date(event.createdDate).toLocaleDateString('en-IN')}
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Progress</span>
                    <span className="font-medium">
                      {event.totalAmount > 0 ? Math.round((event.settledAmount / event.totalAmount) * 100) : 0}%
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${event.totalAmount > 0 ? (event.settledAmount / event.totalAmount) * 100 : 0}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Empty State */}
        {filteredEvents.length === 0 && (
          <div className="text-center py-12">
            <Icon
              name="Calendar"
              size={48}
              className="mx-auto text-muted-foreground mb-4"
            />
            <h3 className="text-lg font-medium text-foreground mb-2">
              No events found
            </h3>
            <p className="text-muted-foreground mb-6">
              {searchTerm || filterStatus !== "all"
                ? "Try adjusting your search or filter criteria."
                : "Create your first event to start tracking shared expenses."}
            </p>
            {!searchTerm && filterStatus === "all" && (
              <Link
                to="/create-event"
                className="inline-flex items-center space-x-2 bg-primary text-primary-foreground px-6 py-3 rounded-md font-medium hover:bg-primary/90 transition-colors"
              >
                <Icon name="Plus" size={20} />
                <span>Create Your First Event</span>
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Events;
